import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import throttle from "lodash/throttle";
import Autocomplete from "@mui/material/Autocomplete";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import {
  SEARCH_PAYEE_ZONES,
  SearchPayeeZonesData,
  SearchPayeeZonesVars,
  ZoneOption,
} from "common/graphql/queries/SearchPayeeZones";
import { GET_ZONE_NAME, GetZoneNameData, GetZoneNameVars } from "common/graphql/queries/GetZoneName";

export interface ZoneAutocompleteProps {
  /** Payee account whose zones are searched */
  payeeAccountId: string;
  /** Ids of the selected zone's records, or empty when nothing is selected */
  value: string[];
  onChange: (zoneIds: string[]) => void;
  label?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  variant?: TextFieldProps["variant"];
  inputRef?: React.Ref<HTMLInputElement>;
}

/** One picker option: a zone name and the ids of every record with that name. */
export interface ZoneNameOption {
  name: string;
  zoneIds: string[];
}

/** Escapes ILIKE's wildcards so typed `%`/`_` match literally, then wraps for a substring match. */
export const toZoneSearchPattern = (input: string): string => `%${input.replace(/[\\%_]/g, "\\$&")}%`;

// Postgres' collation ignores spaces and punctuation and compares digits as text ("1000 Blk" before
// "100-200 S", "600 Blk" after "59 Letitia"), so the picker re-sorts: numbers by value (AUT-6062).
const zoneNameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const byZoneName = (a: ZoneNameOption, b: ZoneNameOption) => zoneNameCollator.compare(a.name, b.name);

/**
 * Groups zones into one option per name. A zone covered by several cameras has a record per camera,
 * all sharing its name, and selecting it should cover all of them (AUT-6062).
 */
export const groupZonesByName = (zones: ZoneOption[]): ZoneNameOption[] => {
  const idsByName = new Map<string, string[]>();
  zones.forEach((zone) => idsByName.set(zone.name, [...(idsByName.get(zone.name) ?? []), zone.id]));
  return Array.from(idsByName, ([name, zoneIds]) => ({ name, zoneIds })).sort(byZoneName);
};

const hasSameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Searchable single-select of a payee's zones (AUT-5863). Searches server-side as the user types
 * rather than loading every zone up front: app_user's zones permission caps any one query at 100
 * rows, and large clients have more zones than that. Takes and returns zone ids so it fits anywhere
 * ids are stored (a form, a URL-persisted grid filter); selected ids it hasn't seen in a search yet
 * (e.g. restored from a URL) get their name looked up separately.
 */
export const ZoneAutocomplete: React.FC<ZoneAutocompleteProps> = ({
  payeeAccountId,
  value,
  onChange,
  label,
  disabled = false,
  size,
  variant,
  inputRef,
}) => {
  const [inputValue, setInputValue] = useState("");
  // The option the user last picked here, so selecting from the list needs no follow-up lookup.
  const [pickedOption, setPickedOption] = useState<ZoneNameOption | null>(null);

  const [searchZones, { data, previousData, loading, error }] = useLazyQuery<
    SearchPayeeZonesData,
    SearchPayeeZonesVars
  >(SEARCH_PAYEE_ZONES);

  const isPicked = pickedOption !== null && hasSameIds(pickedOption.zoneIds, value);

  // `value` is only zone ids, but the input box needs the zone's name. If the user picked the zone
  // from this list, pickedOption already has it. Otherwise -- the selection came in from outside, e.g.
  // a Zone filter restored from the URL on page load, or the filter panel reopened -- look the name up
  // by id. selectedOption below uses whichever source matches `value`.
  const { data: lookupData } = useQuery<GetZoneNameData, GetZoneNameVars>(GET_ZONE_NAME, {
    variables: { zoneIds: value },
    skip: value.length === 0 || isPicked,
  });

  const fetch = useMemo(() => throttle(searchZones, 200), [searchZones]);

  // Cancel any deferred call on unmount: a throttled search scheduled while the user was typing
  // would otherwise fire after the picker is gone.
  useEffect(() => () => fetch.cancel(), [fetch]);

  const search = (text: string) => {
    fetch({ variables: { payeeAccountId, search: toZoneSearchPattern(text) } });
  };

  const selectedOption = useMemo((): ZoneNameOption | null => {
    if (value.length === 0) {
      return null;
    }
    if (isPicked) {
      return pickedOption;
    }
    const name = lookupData?.zones[0]?.name;
    return name ? { name, zoneIds: value } : null;
  }, [value, isPicked, pickedOption, lookupData]);

  // Keep the last results visible while the next search is in flight, and include the selected
  // zone so MUI can always match the value against an option.
  const options = useMemo(() => {
    const grouped = groupZonesByName((data ?? previousData)?.zones ?? []);
    if (selectedOption && !grouped.some((option) => option.name === selectedOption.name)) {
      return [selectedOption, ...grouped].sort(byZoneName);
    }
    return grouped;
  }, [data, previousData, selectedOption]);

  return (
    <Autocomplete<ZoneNameOption, false>
      fullWidth
      size={size}
      disabled={disabled}
      loading={loading}
      value={selectedOption}
      options={options}
      // Results are already filtered server-side; re-filtering here by the input text would also
      // hide everything but the selection once its name fills the input.
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.name}
      // By name: a selection restored from the URL may hold only some of the ids the search found.
      isOptionEqualToValue={(option, val) => option.name === val.name}
      onChange={(_event, newValue) => {
        setPickedOption(newValue);
        onChange(newValue?.zoneIds ?? []);
      }}
      inputValue={inputValue}
      onOpen={() => search(inputValue)}
      onInputChange={(_event, newInputValue, reason) => {
        setInputValue(newInputValue);
        // MUI fires "reset" when a selection rewrites the input to the chosen zone's name;
        // searching for the name we just picked is a wasted round-trip.
        if (reason !== "reset") {
          search(newInputValue);
        }
      }}
      noOptionsText={error ? "Couldn't load zones -- try refreshing" : "No matching zones"}
      renderInput={(params) => <TextField {...params} inputRef={inputRef} variant={variant} label={label} />}
    />
  );
};

export default ZoneAutocomplete;
